/**
 * Ranking config validation schemas.
 * Defines weighted scoring and ranking strategies for products.
 */
import { z } from "zod";
// ============================================================================
// Normalization and Missing Value Strategies
// ============================================================================
/** Normalization methods for ranking */
export const NormalizationMethodSchema = z.enum([
    "min-max",
    "z-score",
    "log",
    "percentile",
    "none",
]);
/** Strategy for handling missing values */
export const MissingValueStrategySchema = z.enum(["exclude", "worst", "best", "average", "zero"]);
// ============================================================================
// Ranking Weights
// ============================================================================
/** Ranking weight for an attribute */
export const RankingWeightSchema = z
    .object({
    attribute: z.string().min(1),
    weight: z.number().min(0).max(1),
    direction: z.enum(["asc", "desc"]),
    normalization: NormalizationMethodSchema,
    missingValueStrategy: MissingValueStrategySchema,
})
    .strict();
/** Ranking factor (alias for weight, used in some contexts) */
export const RankingFactorSchema = RankingWeightSchema.extend({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
}).strict();
// ============================================================================
// Ranking Strategies
// ============================================================================
/** Ranking strategy definition */
export const RankingStrategySchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    isDefault: z.boolean().optional(),
    weights: z.array(RankingWeightSchema).min(1),
    tiebreaker: z.array(z.string()).optional(),
})
    .strict()
    .refine((data) => {
    // Validate that weights sum to approximately 1 (allow small floating point variance)
    const totalWeight = data.weights.reduce((sum, w) => sum + w.weight, 0);
    return Math.abs(totalWeight - 1) < 0.001;
}, {
    message: "Weights must sum to 1",
    path: ["weights"],
});
// ============================================================================
// Ranking Configuration
// ============================================================================
/** Complete ranking configuration */
export const RankingConfigSchema = z
    .object({
    verticalKey: z.string().min(1),
    version: z.number().int().min(1),
    strategies: z.array(RankingStrategySchema).min(1),
    defaultStrategyId: z.string().min(1),
    maxResults: z.number().int().min(1).max(1000).optional(),
    explainResults: z.boolean().optional(),
})
    .strict()
    .refine((data) => {
    // Validate defaultStrategyId exists in strategies
    return data.strategies.some((s) => s.id === data.defaultStrategyId);
}, {
    message: "defaultStrategyId must reference an existing strategy",
    path: ["defaultStrategyId"],
})
    .refine((data) => {
    // Validate exactly one strategy is marked as default (if any are marked)
    const defaultStrategies = data.strategies.filter((s) => s.isDefault === true);
    return defaultStrategies.length <= 1;
}, {
    message: "Only one strategy can be marked as default",
    path: ["strategies"],
});
// ============================================================================
// Ranking Results
// ============================================================================
/** Individual attribute score in ranking */
export const AttributeScoreSchema = z
    .object({
    attribute: z.string(),
    rawValue: z.unknown(),
    normalizedValue: z.number(),
    weight: z.number(),
    contribution: z.number(),
})
    .strict();
/** Explanation factor */
export const ExplanationFactorSchema = z
    .object({
    attribute: z.string(),
    direction: z.enum(["positive", "negative", "neutral"]),
    description: z.string(),
    impact: z.enum(["high", "medium", "low"]),
})
    .strict();
/** Human-readable ranking explanation */
export const RankingExplanationSchema = z
    .object({
    summary: z.string(),
    factors: z.array(ExplanationFactorSchema),
})
    .strict();
/** Ranked product result */
export const RankedProductSchema = z
    .object({
    productId: z.string(),
    rank: z.number().int().min(1),
    score: z.number(),
    strategyId: z.string(),
    attributeScores: z.array(AttributeScoreSchema),
    explanation: RankingExplanationSchema.optional(),
})
    .strict();
